import type { World } from '../sim/types';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { ASHORE_T_PER_KM_YEAR, CLEARED_T_PER_DAY, shoreDebrisT } from '../sim/shoreDebris';
import { COASTLINE_KM } from '../sim/turtles';
import { HEAT_ALERT_DHW, dollars, protectionJob, surveyCost } from '../sim/operations';
import { degreeHeatingWeeks } from '../sim/heatStress';
import { grazingCaughtKgMonth, patrolCut, roughly } from './actionWorth';
import { SHORE_DAYS, SURVEY_DAYS, goalsOf, islandStep, mostExposed, planRoom } from './goals';
import { currentObjective } from '../sim/campaign';
import { escapeHtml } from './format';
import { spareWords } from './runway';

/**
 * The month in the field (M322, the casual year's H4, the main session's playtest: "Nothing happens unless the player
 * makes it happen"; the month's own review: "No survey came home and no rule was made this month"): what the month did
 * on the water, read from the world's own figures, and one decision it puts to the player, with what it costs and what
 * letting it be leaves. GAME CHOICE: on the month's card, which already holds the clock (M289), so a decision comes each
 * month the player keeps the cards; no event the world does not hold, no dice of the interface's own.
 */

/** The grazing fish taken off each island this month at the rules as they stand, kg (`grazingCaughtKgMonth`, M315: within 3% of the death audit's). */
function caught(world: World): Array<{ island: ReefIsland; kg: number }> {
  const m = world.sanctuaries[0]?.management;
  return m ? REEF_ISLANDS.map((island) => ({ island, kg: grazingCaughtKgMonth(world, island, m) })) : [];
}

/** Nets washed ashore over `days` on an island's coast, tonnes (`ASHORE_T_PER_KM_YEAR`, NOAA's Hawaiʻi Large Debris Recovery, cited there). */
const ashore = (island: ReefIsland, days: number): number => (ASHORE_T_PER_KM_YEAR * COASTLINE_KM[island] * days) / 365;

/** A decision the month puts: its question, its way and cost, and what letting it be leaves. */
export interface FieldDecision {
  kind: 'season' | 'patrol' | 'nets' | 'count';
  island: ReefIsland;
  question: string;
  way: { label: string; act: string; cost: string };
  leave: string;
}

/**
 * The decisions the world holds this month, most pressing first: the island whose grazing fish are taken most with
 * nothing protecting them (a patrol), the shore holding the most nets with no cleanup going (a cleanup), an island whose
 * step is its coral's count (a survey). Each only where it can be taken now.
 */
export function fieldDecisions(world: World): FieldDecision[] {
  const out: FieldDecision[] = [];
  const m = world.sanctuaries[0]?.management;
  const ops = world.operations;
  const today = epochDayOf(world);
  // The warm season's own (M323, the casual year's H5): the season goal's way, a baseline count of the island with the
  // most shallow reef before July, or at NOAA's first alert the heat's choice (`goals.ts`'s `season`); the accounts'
  // way is no field decision.
  const season = goalsOf(world).find((g) => g.id === 'season');
  // A count only with room in the plan and its cost in hand, as the others (none the core would refuse, M322's review).
  const countable = planRoom(world) && ops.funds >= surveyCost('reefBenthic', SURVEY_DAYS);
  if (season?.way && season.level !== 'met' && season.way.act !== 'operations' && (!season.way.act.startsWith('area:') || countable)) {
    const heat = season.title === 'Meet the heat';
    out.push({
      kind: 'season', island: REEF_ISLANDS.find((island) => season.way?.act.includes(`:${island}:`)) ?? 'maui',
      question: `${season.title}: ${season.state}`,
      way: { label: season.way.label, act: season.way.act, cost: season.cost ?? (heat ? 'a bleaching check, or the plan kept' : '') },
      leave: heat ? 'Let it be: the heat\'s toll is left for the next coral survey to find.' : 'Let it be: the season\'s toll will have no count before it to be read against.',
    });
  }
  // The first test's count after the heat while it is the campaign's step (M325's review: in a year of heat its season
  // goal holds no way once the heat's choice is made, so November's card had put no count first), the season's island,
  // none while a coral count is out or planned.
  const after = currentObjective(world)?.objective.id === 'surveyAfter';
  const counting = ops.jobs.some((j) => j.survey === 'reefBenthic') || ops.plan.some((p) => p.kind === 'reefBenthic');
  if (after && !counting && countable && !out.some((d) => d.kind === 'season')) {
    const island = mostExposed(world);
    const name = ISLAND_NAMES[island];
    out.push({
      kind: 'season', island,
      question: `Survey the coral after the heat: only a count shows what the warm season did to ${name}'s coral, the most of its reef in the shallows.`,
      way: { label: `Count ${name}'s coral`, act: `area:${island}:reefBenthic`, cost: `${dollars(surveyCost('reefBenthic', SURVEY_DAYS))}, a boat ${SURVEY_DAYS} days` },
      leave: 'Let it be: the warm season\'s toll on the coral goes uncounted.',
    });
  }
  const yearEnd = epochDayFromCivil(civilFromEpochDay(today).year, 12, 31);
  if (m && ops.funds >= 0) {
    const open = caught(world).filter(({ island }) => {
      const i = REEF_ISLANDS.indexOf(island);
      return !m.herbivoreProtection[i] && (m.patrols[i] ?? 0) === 0 && !protectionJob(ops, island);
    }).sort((a, b) => b.kg - a.kg)[0];
    const cut = open ? patrolCut(world, open.island, 1) : null;
    if (open && cut && cut.fewer > 0) {
      const name = ISLAND_NAMES[open.island];
      out.push({
        kind: 'patrol', island: open.island,
        question: `About ${roughly(open.kg)} kg of ${name}'s grazing fish a month are being taken, more than off any island with nothing protecting them.`,
        way: { label: `Protect ${name}'s grazing fish`, act: `respond:${open.island}`, cost: `a patrol keeps about ${roughly(cut.fewer)} kg a month; a protected area more, in months` },
        leave: `Let it be: about ${roughly(open.kg)} kg a month go on being taken.`,
      });
    }
  }
  const going = ops.jobs.some((j) => j.survey === 'shoreCleanup') || ops.plan.some((p) => p.kind === 'shoreCleanup');
  const shore = REEF_ISLANDS.map((island) => ({ island, t: shoreDebrisT(world, island) })).sort((a, b) => b.t - a.t)[0];
  // With room in the boats' plan, as the island's step asks (its review: a full plan refused the card's press).
  if (shore && !going && planRoom(world) && shore.t >= CLEARED_T_PER_DAY * SHORE_DAYS && ops.funds >= surveyCost('shoreCleanup', SHORE_DAYS)) {
    const name = ISLAND_NAMES[shore.island];
    out.push({
      kind: 'nets', island: shore.island,
      question: `About ${Math.round(shore.t)} t of derelict nets lie on ${name}'s shore, the most of any.`,
      way: { label: `Clear ${name}'s shore`, act: `area:${shore.island}:shoreCleanup`, cost: `${dollars(surveyCost('shoreCleanup', SHORE_DAYS))}, a boat ${SHORE_DAYS} days, ${(CLEARED_T_PER_DAY * SHORE_DAYS).toFixed(1)} t cleared` },
      leave: `Let it be: about ${ashore(shore.island, yearEnd - today).toFixed(1)} t more wash onto it by the year's end.`,
    });
  }
  const count = REEF_ISLANDS.map((island) => islandStep(world, island)).find((s) => s.key === 'coral' && s.way);
  // With its cost in hand (its review: offered in debt, refused on its press).
  // Not the season's own count again under another question (its second review: Molokaʻi's count put twice in turn).
  if (count?.way && ops.funds >= surveyCost('reefBenthic', SURVEY_DAYS) && !out.some((d) => d.way.act === count.way?.act)) {
    out.push({
      kind: 'count', island: count.island,
      question: count.state,
      way: { label: count.way.label, act: count.way.act, cost: count.cost ?? '' },
      leave: `Let it be: ${ISLAND_NAMES[count.island]}'s coral stays unknown since its last count.`,
    });
  }
  return out;
}

/** The month's decision: the decisions in turn, one a month, by the month's number, so the same one is not put every month. */
export function monthDecision(world: World, month: number): FieldDecision | null {
  const all = fieldDecisions(world);
  // The season's first at the heat's alert, and on the cards that close March to May, whose count comes home before July
  // (M323; its review: June's card is drawn on 30 June, its 10-day count home after the season began); and November's,
  // its count after the season home before the year's review, the first test's own "Survey the coral after the heat"
  // (M325, the casual year's H7: put in turn, it came on December's card, the fourth chapter never opened); else in turn.
  const season = all.find((d) => d.kind === 'season');
  const closing = ((month - 1) % 12) + 1;
  if (season && (season.question.startsWith('Meet the heat') || (closing >= 3 && closing <= 5) || closing === 11)) return season;
  return all.length === 0 ? null : (all[month % all.length] as FieldDecision);
}

/** The water's heat in a line, the season's months (July to October closing) or wherever heat stress builds. */
function heatLine(world: World, month: number): string {
  const s = world.sanctuaries[0];
  if (!s) return '';
  const dhw = degreeHeatingWeeks(s.heatStress);
  const peak = s.heatStress.peakDhw;
  const closing = ((month - 1) % 12) + 1;
  if (dhw <= 0 && peak <= 0 && (closing < 7 || closing > 10)) return '';
  // Rounded down, so it never reads as the alert below it (its review: 3.96 as "4.0 ... comes at 4").
  const at = (x: number): string => (Math.floor(x * 10) / 10).toFixed(1);
  // The alert as the episode reached it, at its peak, the decision under it the same (its review: today's fallen
  // figure had read "comes at 4" over "Heat stress has reached NOAA's first alert").
  if (peak >= HEAT_ALERT_DHW) return `The heat stress this episode reached ${at(peak)} °C-weeks, past NOAA's first bleaching alert at ${HEAT_ALERT_DHW}; it stands at ${at(dhw)} now.`;
  // Today's figure alone, zero or not (its second review: "no heat stress this season" had been read from today's zero,
  // though heat below the alert leaves the 12 weeks' sum by October and a marine heatwave can come with none); the
  // season's end by its month, the card drawn again on a later day (the same review: "ends today" read on 3 November).
  const ends = closing === 10 ? ' The warm season ends with October.' : '';
  return `The water's heat stress stands at ${at(dhw)} °C-weeks; NOAA's first bleaching alert comes at ${HEAT_ALERT_DHW}.${ends}`;
}

/** The month in the field, at its close: what it did on the water, then its decision. `days` the month's length. */
export function fieldMonthHtml(world: World, month: number, days: number): string {
  const taken = caught(world);
  const total = taken.reduce((n, x) => n + x.kg, 0);
  const most = [...taken].sort((a, b) => b.kg - a.kg)[0];
  const nets = REEF_ISLANDS.reduce((n, island) => n + ashore(island, days), 0);
  const lines = [
    // The fishing's rate at today's fish and rules, a month's (its review: "were taken" had told an estimate as a count).
    most && total > 0 ? `About ${roughly(total)} kg of grazing fish a month are being taken off Maui Nui's reefs, the most off ${ISLAND_NAMES[most.island]} (${roughly(most.kg)} kg).` : '',
    `About ${nets.toFixed(1)} t of derelict nets washed onto its shores.`,
    // The water's heat, through the warm season's months and whenever it is stressed (M323): NOAA Coral Reef Watch's
    // Degree Heating Weeks, its first alert at 4 (`HEAT_ALERT_DHW`).
    heatLine(world, month),
    // The year's money against what is owed to the next grant (M324, the casual year's H6: "Money stops mattering on 1
    // July"), so the decision under it reads as a trade.
    spareWords(world),
  ].filter(Boolean);
  const d = monthDecision(world, month);
  const decision = d ? `<h4>This month's decision</h4><p>${escapeHtml(d.question)}</p>
      <div class="home-actions"><button class="primary" data-act="goal" data-goal="${escapeHtml(d.way.act)}"><strong>${escapeHtml(d.way.label)}</strong><span>${escapeHtml(d.way.cost)}</span></button>
      <button data-act="month-continue"><strong>Let it be</strong><span>${escapeHtml(d.leave.replace(/^Let it be: /, ''))}</span></button></div>` : '';
  return `<section class="month-field"><h4>In the field</h4>${lines.map((l) => `<p>${escapeHtml(l)}</p>`).join('')}${decision}</section>`;
}
